import {
  REFERRAL_FIXTURES,
  REFERRAL_STAGES,
  expectedReferralRead,
  expectedReferralCopyRead,
  assertReferralAccess
} from './referralAccessPlan.js'

// The adapter performs real SDK requests; injected in tests so failure and
// cleanup paths are exercised without pretending mocks are hosted evidence.
export async function runReferralAccessWorkflow(ctx, step, io) {
  const reason = io.blockReason()
  if (reason) {
    step.block('referral access setup', reason)
    return
  }
  let failure
  try {
    await step('prepare three disposable students and wait for access worker', io.prepare)
    for (const stage of REFERRAL_STAGES) {
      if (stage !== 'before consent') {
        await step(`${stage}: save consent and await access refresh`, () =>
          io.setConsent(stage === 'consent saved')
        )
      }
      await step(`${stage}: verify signed-in role`, io.assertRole)
      for (const fixture of REFERRAL_FIXTURES) {
        const allowed = expectedReferralRead(ctx.roleClaim, fixture, stage)
        for (const target of ['student', 'Storage file', 'file URL function', 'legacy download']) {
          await step(`${stage} / ${fixture} / ${target}: ${allowed ? 'allow' : 'deny'}`, () =>
            assertReferralAccess(allowed, () => io.read(fixture, target))
          )
        }
        for (const collection of ['extendedAccessStudents', 'testingAccommodations']) {
          const copyAllowed = expectedReferralCopyRead(
            ctx.roleClaim,
            fixture,
            stage,
            collection,
            io.testingAccess()
          )
          await step(
            `${stage} / ${fixture} / ${collection}: ${copyAllowed ? 'allow' : 'deny'}`,
            () => assertReferralAccess(copyAllowed, () => io.readCopy(fixture, collection))
          )
        }
      }
      await step(`${stage}: student lists contain exactly the allowed test students`, async () => {
        const actual = [...(await io.list())].sort()
        const expected = REFERRAL_FIXTURES.filter((key) =>
          expectedReferralRead(ctx.roleClaim, key, stage)
        ).sort()
        if (JSON.stringify(actual) !== JSON.stringify(expected)) {
          throw new Error(
            `Student list mismatch: expected ${expected.join(', ')}, received ${actual.join(', ')}`
          )
        }
        return `${actual.length} expected test students; active 504 preserved`
      })
    }
  } catch (error) {
    failure = error
  }
  try {
    await step('remove this scenario’s disposable documents and files', io.cleanup)
  } catch (error) {
    failure ||= error
  }
  // Even cleanup failure must not strand the user in the setup role.
  try {
    await step('restore and verify starting role', io.restore)
  } catch (error) {
    ctx.authInvalid = true
    failure = error
  }
  if (failure) throw failure
}
